import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../api/apiService';

// Initial state
const initialState = {
  bookings: [],
  currentBooking: null,
  currentStep: 1,
  bookingId: null,
  loading: false,
  error: null,
  stats: {
    total: 0,
    pending: 0,
    confirmed: 0,
    inProgress: 0,
    completed: 0,
    cancelled: 0,
  },
};

// Step-wise Booking Async Thunks
export const createBookingStep1 = createAsyncThunk(
  'bookings/createBookingStep1',
  async (step1Data, { rejectWithValue }) => {
    try {
      const response = await apiService.createBookingStep1(step1Data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create booking step 1');
    }
  }
);

export const updateBookingStep2 = createAsyncThunk(
  'bookings/updateBookingStep2',
  async ({ id, step2Data }, { rejectWithValue }) => {
    try {
      const response = await apiService.updateBookingStep2(id, step2Data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update booking step 2');
    }
  }
);

export const updateBookingStep3 = createAsyncThunk(
  'bookings/updateBookingStep3',
  async ({ id, step3Data }, { rejectWithValue }) => {
    try {
      const response = await apiService.updateBookingStep3(id, step3Data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update booking step 3');
    }
  }
);

export const updateBookingStep4 = createAsyncThunk(
  'bookings/updateBookingStep4',
  async ({ id, step4Data }, { rejectWithValue }) => {
    try {
      const response = await apiService.updateBookingStep4(id, step4Data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update booking step 4');
    }
  }
);

// General Booking Async Thunks
export const fetchBookings = createAsyncThunk(
  'bookings/fetchBookings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.getBookings();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch bookings');
    }
  }
);

export const fetchBookingById = createAsyncThunk(
  'bookings/fetchBookingById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.getBookingById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch booking');
    }
  }
);

export const fetchBookingsByMobile = createAsyncThunk(
  'bookings/fetchBookingsByMobile',
  async (mobile, { rejectWithValue }) => {
    try {
      const response = await apiService.getBookingsByMobile(mobile);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch bookings by mobile');
    }
  }
);

export const updateBookingStatus = createAsyncThunk(
  'bookings/updateBookingStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await apiService.updateBookingStatus(id, status);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update booking status');
    }
  }
);

export const deleteBooking = createAsyncThunk(
  'bookings/deleteBooking',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.deleteBooking(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete booking');
    }
  }
);

// Booking slice
const bookingSlice = createSlice({
  name: 'bookings',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentBooking: (state) => {
      state.currentBooking = null;
    },
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },
    setBookingId: (state, action) => {
      state.bookingId = action.payload;
    },
    resetBookingFlow: (state) => {
      state.currentStep = 1;
      state.bookingId = null;
      state.currentBooking = null;
      state.error = null;
    },
    updateBookingInList: (state, action) => {
      const { id, updates } = action.payload;
      const index = state.bookings.findIndex(booking => booking.id === id);
      if (index !== -1) {
        state.bookings[index] = { ...state.bookings[index], ...updates };
      }
    },
    calculateStats: (state) => {
      const stats = {
        total: state.bookings.length,
        pending: state.bookings.filter(b => b.status === 'pending').length,
        confirmed: state.bookings.filter(b => b.status === 'confirmed').length,
        inProgress: state.bookings.filter(b => b.status === 'in-progress').length,
        completed: state.bookings.filter(b => b.status === 'completed').length,
        cancelled: state.bookings.filter(b => b.status === 'cancelled').length,
      };
      state.stats = stats;
    },
  },
  extraReducers: (builder) => {
    builder
      // Step 1: Create booking
      .addCase(createBookingStep1.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBookingStep1.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBooking = action.payload;
        state.bookingId = action.payload.id;
        state.currentStep = 2;
        state.error = null;
      })
      .addCase(createBookingStep1.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Step 2: Update address details
      .addCase(updateBookingStep2.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBookingStep2.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBooking = action.payload;
        state.currentStep = 3;
        state.error = null;
      })
      .addCase(updateBookingStep2.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Step 3: Update scrap details
      .addCase(updateBookingStep3.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBookingStep3.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBooking = action.payload;
        state.currentStep = 4;
        state.error = null;
      })
      .addCase(updateBookingStep3.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Step 4: Final booking details
      .addCase(updateBookingStep4.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateBookingStep4.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBooking = action.payload;
        state.bookings.push(action.payload);
        state.currentStep = 1;
        state.bookingId = null;
        state.error = null;
        // Recalculate stats
        const stats = {
          total: state.bookings.length,
          pending: state.bookings.filter(b => b.status === 'pending').length,
          confirmed: state.bookings.filter(b => b.status === 'confirmed').length,
          inProgress: state.bookings.filter(b => b.status === 'in-progress').length,
          completed: state.bookings.filter(b => b.status === 'completed').length,
          cancelled: state.bookings.filter(b => b.status === 'cancelled').length,
        };
        state.stats = stats;
      })
      .addCase(updateBookingStep4.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch bookings
      .addCase(fetchBookings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings = action.payload;
        // Calculate stats after fetching
        const stats = {
          total: action.payload.length,
          pending: action.payload.filter(b => b.status === 'pending').length,
          confirmed: action.payload.filter(b => b.status === 'confirmed').length,
          inProgress: action.payload.filter(b => b.status === 'in-progress').length,
          completed: action.payload.filter(b => b.status === 'completed').length,
          cancelled: action.payload.filter(b => b.status === 'cancelled').length,
        };
        state.stats = stats;
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch booking by ID
      .addCase(fetchBookingById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBookingById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBooking = action.payload;
      })
      .addCase(fetchBookingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Fetch bookings by mobile
      .addCase(fetchBookingsByMobile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBookingsByMobile.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings = action.payload;
        // Calculate stats after fetching
        const stats = {
          total: action.payload.length,
          pending: action.payload.filter(b => b.status === 'pending').length,
          confirmed: action.payload.filter(b => b.status === 'confirmed').length,
          inProgress: action.payload.filter(b => b.status === 'in-progress').length,
          completed: action.payload.filter(b => b.status === 'completed').length,
          cancelled: action.payload.filter(b => b.status === 'cancelled').length,
        };
        state.stats = stats;
      })
      .addCase(fetchBookingsByMobile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      
      // Update booking status
      .addCase(updateBookingStatus.fulfilled, (state, action) => {
        const index = state.bookings.findIndex(booking => booking.id === action.payload.id);
        if (index !== -1) {
          state.bookings[index] = action.payload;
        }
        // Recalculate stats
        const stats = {
          total: state.bookings.length,
          pending: state.bookings.filter(b => b.status === 'pending').length,
          confirmed: state.bookings.filter(b => b.status === 'confirmed').length,
          inProgress: state.bookings.filter(b => b.status === 'in-progress').length,
          completed: state.bookings.filter(b => b.status === 'completed').length,
          cancelled: state.bookings.filter(b => b.status === 'cancelled').length,
        };
        state.stats = stats;
      })
      
      // Delete booking
      .addCase(deleteBooking.fulfilled, (state, action) => {
        state.bookings = state.bookings.filter(booking => booking.id !== action.payload);
        // Recalculate stats
        const stats = {
          total: state.bookings.length,
          pending: state.bookings.filter(b => b.status === 'pending').length,
          confirmed: state.bookings.filter(b => b.status === 'confirmed').length,
          inProgress: state.bookings.filter(b => b.status === 'in-progress').length,
          completed: state.bookings.filter(b => b.status === 'completed').length,
          cancelled: state.bookings.filter(b => b.status === 'cancelled').length,
        };
        state.stats = stats;
      });
  },
});

export const { 
  clearError, 
  clearCurrentBooking, 
  setCurrentStep, 
  setBookingId, 
  resetBookingFlow, 
  updateBookingInList, 
  calculateStats 
} = bookingSlice.actions;
export default bookingSlice.reducer;
